import { useLocation } from "wouter";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/use-auth";
import { MessageSquare, Calendar, FolderOpen, LogOut, Shield } from "lucide-react";

export default function Community() {
  const { user, isAuthenticated, isLoading, logout } = useAuth();
  const [, navigate] = useLocation();

  if (isLoading) {
    return (
      <>
        <Navbar />
        <main id="main-content" className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-muted-foreground" role="status" aria-live="polite">Loading...</div>
        </main>
      </>
    );
  }

  if (!isAuthenticated) {
    navigate("/login");
    return null;
  }

  const sections = [
    {
      title: "Forum",
      description: "Discuss strategies, ask questions, and share insights with fellow founders and leaders.",
      icon: MessageSquare,
      href: "/community/forum",
      color: "text-blue-400",
      bgColor: "bg-blue-400/10",
    },
    {
      title: "Calendar",
      description: "Upcoming events, webinars, workshops, and community meetups.",
      icon: Calendar,
      href: "/community/calendar",
      color: "text-emerald-400",
      bgColor: "bg-emerald-400/10",
    },
    {
      title: "Resources",
      description: "Templates, guides, tools, and frameworks to help you build and grow.",
      icon: FolderOpen,
      href: "/community/resources",
      color: "text-amber-400",
      bgColor: "bg-amber-400/10",
    },
  ];

  return (
    <>
      <Navbar />
      <main id="main-content" className="pt-20 min-h-screen">
        <section className="py-16 border-b border-white/5">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-3xl font-display font-bold">Community Hub</h1>
                  {user?.isAdmin && (
                    <span className="bg-primary/10 text-primary text-xs px-2 py-1 rounded-full flex items-center gap-1">
                      <Shield size={12} aria-hidden="true" /> Admin
                    </span>
                  )}
                </div>
                <p className="text-muted-foreground">
                  Welcome back, <span className="text-foreground font-medium">{user?.displayName}</span>
                </p>
              </div>
              <Button
                variant="outline"
                className="border-white/10"
                onClick={() => logout.mutate(undefined, { onSuccess: () => navigate("/") })}
                data-testid="button-logout"
              >
                <LogOut size={16} className="mr-2" aria-hidden="true" /> Sign Out
              </Button>
            </div>
          </div>
        </section>

        <section className="py-16" aria-label="Community sections">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <h2 className="sr-only">Community Sections</h2>
            <div className="grid md:grid-cols-3 gap-6" role="list">
              {sections.map((section) => (
                <Card
                  key={section.title}
                  className="bg-card border-white/10 hover:border-primary/30 transition-all duration-300 cursor-pointer group"
                  onClick={() => navigate(section.href)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); navigate(section.href); } }}
                  tabIndex={0}
                  role="listitem"
                  aria-label={`${section.title} - ${section.description}`}
                  data-testid={`card-community-${section.title.toLowerCase()}`}
                >
                  <CardContent className="p-8">
                    <div className={`w-14 h-14 rounded-2xl ${section.bgColor} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`} aria-hidden="true">
                      <section.icon className={section.color} size={28} />
                    </div>
                    <h3 className="text-xl font-display font-semibold mb-3">{section.title}</h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">{section.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
